/**
 * Voice Memo Package Header Component
 * Editable package name with rename functionality
 */

import { useState } from 'react';
import { Edit2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusBadge } from './voice-memo-status-badge';
import type { VoiceMemoPackage } from './types';

interface VoiceMemoPackageHeaderProps {
  package: VoiceMemoPackage;
  onUpdateName?: (packageId: string, newName: string) => void;
}

export function VoiceMemoPackageHeader({
  package: pkg,
  onUpdateName
}: VoiceMemoPackageHeaderProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(pkg.packageName || `${pkg.packageDate} Voice Memos`);

  const handleSave = () => {
    if (onUpdateName && editValue.trim()) {
      onUpdateName(pkg.id, editValue.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(pkg.packageName || `${pkg.packageDate} Voice Memos`);
    setIsEditing(false);
  };

  return (
    <div className="flex items-center justify-between mb-2">
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 px-3 py-1.5 text-xl font-semibold bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Package name..."
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSave();
                } else if (e.key === 'Escape') {
                  handleCancel();
                }
              }}
            />
            <Button
              size="sm"
              variant="default"
              onClick={handleSave}
              className="h-7"
            >
              <Check className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleCancel}
              className="h-7"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 group">
            <h2 className="text-xl font-semibold break-words">
              {pkg.packageName || `${pkg.packageDate} Voice Memos`}
            </h2>
            {onUpdateName && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => setIsEditing(true)}
                aria-label="Rename package"
              >
                <Edit2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        )}
        <p className="text-sm text-muted-foreground mt-1">
          {pkg.items.length} memos • {pkg.packageDate} • {pkg.groupingType} grouping
        </p>
      </div>
      <StatusBadge status={pkg.status === 'analyzed' ? 'analyzed' : 'transcribed'} />
    </div>
  );
}
